import LinkOutlined from '@ant-design/icons/LinkOutlined'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import { List, Popover, Spin, Tag } from 'antd'
import { OntologyValueType } from 'features/dynamicOntology'
import { useLibraDocument } from 'features/libra/document/LibraDocumentContext'
import { useFieldValueLookup } from 'features/libra/fieldValues/FieldValueLookupContext'
import { ValueLookupValueType } from 'features/libra/stepComponents/conditionalForm/api/conditionalFormApi'
import { Dispatch, FC, SetStateAction } from 'react'
import styled from 'styled-components'
import { GeckoEntry, RolodexEntryType } from 'types'

interface ExternalValueLookupProps {
  field: string
  setValue: Dispatch<SetStateAction<OntologyValueType>>
}

export const ExternalValueLookup: FC<ExternalValueLookupProps> = ({
  field,
  setValue,
}) => {
  const { values, isLoading } = useFieldValueLookup(field)
  const popoverContent = (
    <div style={{ maxHeight: '300px', overflowY: 'auto', minWidth: '200px' }}>
      <List
        loading={isLoading}
        locale={{ emptyText: 'No values found' }}
        dataSource={[...values]}
        renderItem={(value) => (
          <ExternalValueLookupListItem
            value={value}
            onClick={() => setValue(value.value as OntologyValueType)}
          />
        )}
      />
    </div>
  )

  return isLoading || values.length ? (
    <Popover
      content={values.length ? popoverContent : 'No external values found'}
      title='Available Values'
      trigger='hover'
      open={isLoading ? false : undefined}>
      {/* Ant Design v6 requirement: Spin must be wrapped in a span when nested inside Popover.
          See https://github.com/ant-design/ant-design/issues/41994 for details. */}
      <span>
        <Spin indicator={<LoadingOutlined />} spinning={isLoading}>
          <Tag
            color={values.length ? 'blue' : 'default'}
            style={{ cursor: values.length ? 'pointer' : 'default' }}>
            ...
          </Tag>
        </Spin>
      </span>
    </Popover>
  ) : (
    <></>
  )
}

const ExternalValueLookupListItem: FC<{
  value: ValueLookupValueType
  onClick?: () => void
}> = ({ value: { sourceName, value, nameEntry }, onClick }) => {
  const { setSelectedDocumentId } = useLibraDocument()
  const linkGenerator = TYPE_TO_LINK_GENERATOR[nameEntry.type]
  const link = linkGenerator?.(nameEntry)

  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    setSelectedDocumentId(nameEntry?.id)
  }

  return (
    <StyledListItem onClick={onClick}>
      <ValueTitle>{String(value)}</ValueTitle>
      {sourceName ? <SourceTag>{sourceName}</SourceTag> : null}
      {link || nameEntry?.id ? (
        <StyledLinkButton onClick={handleLinkClick}>
          <LinkOutlined style={{ fontSize: 16 }} />
        </StyledLinkButton>
      ) : null}
    </StyledListItem>
  )
}

const TYPE_TO_LINK_GENERATOR: Partial<
  Record<RolodexEntryType, (entry: GeckoEntry) => string | undefined>
> = {
  [RolodexEntryType.REPORT]: (entry) =>
    entry.tags.unit
      ? `/document-upload/${entry.tags.unit}?document=${entry.id}`
      : undefined,
}

const StyledListItem = styled(List.Item)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  padding: 8px 12px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) =>
      theme.forecast?.hoverGhostButton ||
      theme.forecast?.background2 ||
      '#f5f5f5'};
  }
`

const ValueTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast?.defaultText || 'inherit'};
`

const SourceTag = styled(Tag)`
  font-size: 11px;
  line-height: 18px;
  color: ${({ theme }) => theme.forecast?.secondaryText || '#595959'};
  background-color: ${({ theme }) =>
    theme.forecast?.cardDividerBackground ||
    theme.forecast?.background2 ||
    '#f0f0f0'};
  border: none;
  padding: 0 8px;
`

const StyledLinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${({ theme }) => theme.forecast?.primary || '#1890ff'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.8;
  }
`
